import { Box, Card, CardHeader, Divider, IconButton, Link, Tooltip, Typography } from "@mui/material";
import { useListContext } from "ra-core";
import { FC } from "react";
import { Link as RouterLink } from 'react-router-dom'
import { RecPatch } from "~/dataProvider/tools";
import { BackupJobStatus, DatabaseResponseDto } from "~/gen/api/backup";
import { Pagination as DefaultPagination } from '~/components/list/pagination'
import { useLogViewerContext } from "../backups/LogViewer";
import { FunctionField } from "../field";
import { Datagrid } from "../list/datagrid";
import Scrollbar from "../Scrollbar";
import ArrowRightIcon from '~/icons/ArrowRight';
import SizeTextField from "../field/SizeTextField";
import { genUrlWithHashid } from "~/utils/hashid";
import { useUserTimeFormat } from "~/utils/userTimeFormat";
import { AtopFuncArea, SearchField, FilterField, FilterOption } from "../list/datagridFunctionWidgets";
import DatabaseNameField from "../databases/DatabaseNameField";
import { EncryptedDatabasesContextProvider } from "../databases/useEncryptedDatabases";


const statusOptions = [
  { label: 'No Backup', value: BackupJobStatus.NoBackup },
  { label: 'Backup In Progress', value: BackupJobStatus.InProgress },
  { label: 'Backup Failed', value: BackupJobStatus.Failed },
  { label: 'Backup Received', value: BackupJobStatus.Received },
]

const DatabaseTable: FC = () => {
  const listContext = useListContext()
  const { showLogInViewer } = useLogViewerContext()
  
  const format = useUserTimeFormat()
  
  return (
    <EncryptedDatabasesContextProvider>
      <Card>
        <CardHeader title="Databases" />
        <Divider />
        <AtopFuncArea>
          <SearchField 
            sourceKey="dbName"
            match={(d: DatabaseResponseDto, fval:string) => !!d.name && d.name.toLowerCase().includes(fval.trim().toLowerCase())}
            sx={{ width: 280 }}
            TextFieldProps={{ placeholder: 'Search by Database Name' }}
          />
          <FilterField 
            label="Status" 
            filterOptions={statusOptions} 
            sourceKey="status"
            match={(d: DatabaseResponseDto, fval:FilterOption) => d.backupJobStatus === fval.value}
            sx={{ width: 190 }}
          />
        </AtopFuncArea>
        <Scrollbar>
          <Box sx={{ minWidth: 1150 }}>
            <Datagrid
              hasBulkActions={false} 
              {...listContext}
              sx={{ 
                '& th' : { py: 2 },
                '& td' : { py: 2 },
              }}
            >
              <DatabaseNameField label="Name" showLink={true} />
              <SizeTextField source="sizeText" numericSource="size" label="Size" emptyText="N/A" />
              <FunctionField 
                label="Last Backup Received"
                source="lastBackupTime"
                render={(d?: DatabaseResponseDto & RecPatch) => (
                  <Typography
                    component="span"
                    variant="body2"
                    sx={{
                      color:
                        d?.backupJobStatus === BackupJobStatus.Failed ? "error.main" 
                        : d?.backupJobStatus === BackupJobStatus.Received ? "success.main" 
                        : d?.backupJobStatus === BackupJobStatus.InProgress ? "info.main" 
                        : "inherit"
                    }}
                  >
                    {d?.lastBackupTime 
                      ? format(d.lastBackupTime, 'Ppp')
                      : 'N/A'}
                  </Typography>
                )}
              />
              <FunctionField 
                label="Status" 
                sortable={false} 
                render={(d?: DatabaseResponseDto & RecPatch) => (
                  <>
                    <Typography
                      component="p"
                      sx={{
                        color:
                          d?.backupJobStatus === BackupJobStatus.Failed ? "error.main" 
                          : d?.backupJobStatus === BackupJobStatus.Received ? "success.main" 
                          : d?.backupJobStatus === BackupJobStatus.InProgress ? "info.main" 
                          : "inherit"
                      }}
                      variant="body2"
                    >
                      {d?.backupJobStatusText}
                    </Typography>
                    {d?.backupJobStatus === BackupJobStatus.Failed ?
                      <Link
                        component="button"
                        color="secondary"
                        underline="hover"
                        onClick={(e:any) => showLogInViewer(e, d.backupFailedLog || '')}
                        sx={{ cursor: 'pointer'}}
                      >
                        <Typography
                          variant="caption"
                        >
                          (View Log)
                        </Typography>
                      </Link> : null
                    }
                  </>
                )}
              />
              <FunctionField 
                label="Action"
                sortable={false}
                render={(d?: DatabaseResponseDto & RecPatch) => (
                  <Tooltip title="Detail">
                    <IconButton 
                      sx={{ 
                        color: 'text.primary' as any, 
                        '&:hover': { backgroundColor: 'action.hover' },
                      }}
                      component={RouterLink}
                      to={genUrlWithHashid(`/databases/$0/show`, d?.databaseId || 0, d?.name)}
                    >
                      <ArrowRightIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              />
            </Datagrid>
          </Box>
        </Scrollbar>
        {//@ts-ignore 
        <DefaultPagination {...listContext} />}
      </Card>
    </EncryptedDatabasesContextProvider>
          

  )
}

export default DatabaseTable;
